import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { fetchEvents } from '../utils/api';
import EventCard from '../components/EventCard';
import EventCardSkeleton from '../components/EventCardSkeleton';
import FilterBar from '../components/FilterBar';
import { AdminBanner } from '../components/AdminBanner';

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
};

const stagger = {
  show: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1
    }
  }
};

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    location: 'all',
    type: 'all',
    month: 'current'
  });

  useEffect(() => {
    loadEvents();
  }, [filters]);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const data = await fetchEvents(filters);
      setEvents(data.events);
    } catch (error) {
      console.error('Failed to load events:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <title>Upcoming Events | KGOB</title>
      <meta name="description" content="Join KGOB for networking, community events, and professional development in Winston-Salem, Charlotte, and Mooresville." />

      <AdminBanner />

      {/* Hero Section - KGOB Style - Fully Responsive */}
      <section className="relative overflow-hidden bg-[linear-gradient(180deg,#0B2240_0%,#07182F_100%)] text-white noise-overlay">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-24">
          <div className="lg:grid lg:grid-cols-12 lg:items-center lg:gap-10">
            <motion.div
              className="lg:col-span-6 space-y-4 sm:space-y-6"
              variants={fadeUp}
              initial="hidden"
              animate="show"
            >
              <p className="uppercase tracking-[0.18em] text-[10px] sm:text-xs text-[#E3C96E]">
                KGOB EVENTS
              </p>
              <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight tracking-[-0.01em]">
                Events that bring leaders, communities, and conversations together.
              </h1>
              <p className="text-white/80 text-base sm:text-lg max-w-xl">
                Join KGOB for curated networking, learning, and community events designed for professionals, partners, and neighbors who value connection and impact.
              </p>
            </motion.div>
            
            {/* Glass CTA Cards - KGOB Style - Responsive */}
            <div className="lg:col-span-6 grid grid-cols-2 gap-3 sm:gap-4 mt-8 lg:mt-0">
              {[
                { title: 'Networking', desc: 'Monthly breakfasts', icon: '🤝' },
                { title: 'Coffee Fridays', desc: 'Drop in anytime', icon: '☕' },
                { title: 'Community', desc: 'Charity & events', icon: '❤️' },
                { title: 'All Events', desc: 'Browse calendar', icon: '📅' },
              ].map((card, i) => (
                <motion.div
                  key={card.title}
                  className="glass-panel rounded-xl sm:rounded-2xl p-4 sm:p-5 group cursor-pointer transition-all duration-300"
                  variants={fadeUp}
                  initial="hidden"
                  animate="show"
                  transition={{ delay: i * 0.1 }}
                  whileHover={{ y: -4 }}
                  data-testid={`hero-cta-${card.title.toLowerCase().replace(' ', '-')}`}
                >
                  <div className="text-2xl sm:text-3xl mb-1 sm:mb-2">{card.icon}</div>
                  <h3 className="font-medium text-base sm:text-lg mb-0.5 sm:mb-1">{card.title}</h3>
                  <p className="text-white/70 text-xs sm:text-sm">{card.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="bg-[#F7F6F2] py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Filters */}
          <FilterBar filters={filters} onChange={setFilters} />

          {/* Loading State with Skeletons */}
          {loading && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <EventCardSkeleton key={i} />
              ))}
            </div>
          )}

          {/* Events Grid */}
          {!loading && events.length > 0 && (
            <motion.div
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
              variants={stagger}
              initial="hidden"
              animate="show"
            >
              {events.map(event => (
                <motion.div key={event.id} variants={fadeUp}>
                  <EventCard event={event} />
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* Empty State */}
          {!loading && events.length === 0 && (
            <div className="text-center py-12">
              <p className="text-[#4C5A70] text-lg">No events found matching your filters.</p>
              <button
                onClick={() => setFilters({ location: 'all', type: 'all', month: 'current' })}
                className="mt-4 text-[#1FA3A3] hover:underline"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
